import React, { useEffect, useRef, useState } from 'react'
import { Alert, Button, Form, Modal, Spinner } from 'react-bootstrap'
import { FaBirthdayCake } from 'react-icons/fa'
import { LuImage, LuSend } from 'react-icons/lu'
import { SweetAlert } from '../../components/sweetalert'
import Birthdays from '../../services/Birthdays'
import { fillNames } from '../../utilities/birthdays'
import NameTokenButtons from './NameTokenButtons'

const IMAGE_MAX_SIZE_MB = 5
const MESSAGE_MAX_LENGTH = 5000

const errorMessage = (error, fallback) => error?.response?.data?.message || error?.message || fallback

const BirthdayGreetingsModal = ({ show, onHide, defaultTestEmail = '', onChanged }) => {
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [config, setConfig] = useState(null)
  const [form, setForm] = useState({ enabled: false, subject: '', message: '' })
  const [imageChange, setImageChange] = useState({ kind: 'unchanged' })
  const [testEmail, setTestEmail] = useState(defaultTestEmail)
  const [sendingTest, setSendingTest] = useState(false)
  const [confirmDispatch, setConfirmDispatch] = useState(false)
  const [dispatching, setDispatching] = useState(false)
  const fileRef = useRef(null)
  const confirmRef = useRef(null)
  const messageRef = useRef(null)

  useEffect(() => {
    if (!show) return

    setLoading(true)
    setConfig(null)
    setImageChange({ kind: 'unchanged' })
    setConfirmDispatch(false)
    setTestEmail(defaultTestEmail)

    Birthdays.findConfig()
      .then(({ data }) => {
        setConfig(data)
        setForm({ enabled: data.enabled, subject: data.subject, message: data.message })
      })
      .catch((error) => {
        SweetAlert.error(errorMessage(error, 'Não foi possível carregar a configuração.'), 'E-mail de aniversário')
        onHide()
      })
      .finally(() => setLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show])

  // Libera a prévia da imagem escolhida
  useEffect(() => {
    return () => { if (imageChange.kind === 'new') URL.revokeObjectURL(imageChange.preview) }
  }, [imageChange])

  // A confirmação fica no fim do corpo: rola até ela
  useEffect(() => {
    if (confirmDispatch) confirmRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
  }, [confirmDispatch])

  const busy = loading || saving || sendingTest || dispatching
  const dirty = !!config && ((form.enabled !== config.enabled) || (form.subject !== config.subject) || (form.message !== config.message) || (imageChange.kind !== 'unchanged'))
  const imagePreview = (imageChange.kind === 'new') ? imageChange.preview : ((imageChange.kind === 'remove') ? '' : config?.image_url)

  function handleSelectImage(e) {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return

    if (!file.name.match(/\.(jpg|jpeg|png|gif)$/i))
      return SweetAlert.error('Escolha uma imagem JPG, PNG ou GIF.', 'Imagem do e-mail')

    if (file.size > IMAGE_MAX_SIZE_MB * 1000000)
      return SweetAlert.error(`A imagem deve ter no máximo ${IMAGE_MAX_SIZE_MB} MB.`, 'Imagem do e-mail')

    setImageChange({ kind: 'new', file, preview: URL.createObjectURL(file) })
  }

  async function handleSave() {
    if (!form.subject.trim()) return SweetAlert.error('Informe o assunto do e-mail.', 'E-mail de aniversário')
    if (!form.message.trim()) return SweetAlert.error('Escreva a mensagem do e-mail.', 'E-mail de aniversário')

    setSaving(true)
    try {
      await Birthdays.updateConfig({ enabled: form.enabled, subject: form.subject, message: form.message })

      if (imageChange.kind === 'new') {
        const formData = new FormData()
        formData.append('photo', imageChange.file)
        await Birthdays.updateImage(formData)
      }

      if (imageChange.kind === 'remove') await Birthdays.deleteImage()

      SweetAlert.success('Configuração salva com sucesso!', 'E-mail de aniversário')
      onHide()
    } catch (error) {
      SweetAlert.error(errorMessage(error, 'Não foi possível salvar a configuração.'), 'E-mail de aniversário')
    } finally {
      setSaving(false)
    }
  }

  async function handleSendTest() {
    if (!testEmail.trim()) return SweetAlert.error('Informe um e-mail para enviar o teste.', 'E-mail de teste')

    setSendingTest(true)
    try {
      const { data } = await Birthdays.sendTest(testEmail.trim())
      SweetAlert.success(`E-mail de teste enviado para ${data.email}.`, 'E-mail de teste')
    } catch (error) {
      SweetAlert.error(errorMessage(error, 'Não foi possível enviar o e-mail de teste.'), 'E-mail de teste')
    } finally {
      setSendingTest(false)
    }
  }

  async function handleDispatch() {
    setDispatching(true)
    try {
      const { data } = await Birthdays.dispatch()

      if (data.total === 0) SweetAlert.info('Nenhum paciente ativo faz aniversário hoje.', 'Enviar agora')
      else SweetAlert[data.failed > 0 ? 'warn' : 'success'](
        `Enviados: ${data.sent}<br/>Já tinham recebido: ${data.already_sent}<br/>Sem e-mail: ${data.skipped}<br/>Falhas: ${data.failed}`,
        `Aniversariantes de hoje: ${data.total}`,
        8000,
      )

      setConfirmDispatch(false)
      onChanged()
    } catch (error) {
      SweetAlert.error(errorMessage(error, 'Não foi possível enviar os e-mails.'), 'Enviar agora')
    } finally {
      setDispatching(false)
    }
  }

  return (
    <Modal show={show} onHide={() => !busy && onHide()} size='lg' fullscreen='sm-down' scrollable centered>
      <Modal.Header closeButton={!busy}>
        <Modal.Title className='d-flex align-items-center gap-2 fs-5'>
          <FaBirthdayCake style={{ color: 'var(--birthdays-primary)' }} /> E-mail de aniversário
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className='d-flex flex-column gap-3'>
        {loading || !config ?
          <div className='d-flex justify-content-center py-5'><Spinner animation='border' size='sm' /></div>
          :
          <>
            <p className='text-muted m-0' style={{ fontSize: 14 }}>
              O sistema envia automaticamente, a partir das 06:00, um e-mail para cada paciente ativo que faz aniversário no dia e tem e-mail cadastrado. Cada paciente recebe no máximo um e-mail por ano.
            </p>

            <div className='border rounded p-3'>
              <Form.Check
                type='switch'
                id='birthday-greetings-enabled'
                label={<span className='fw-semibold'>Envio automático ativo</span>}
                checked={form.enabled}
                disabled={busy}
                onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
              />
              <small className='text-muted'>Desativado, nenhum e-mail sai sozinho. O botão "Enviar agora" continua funcionando.</small>
            </div>

            <Form.Group>
              <Form.Label className='fw-semibold'>Assunto do e-mail <span className='text-danger'>*</span></Form.Label>
              <Form.Control
                value={form.subject}
                maxLength={200}
                disabled={busy}
                placeholder='Feliz aniversário!'
                onChange={(e) => setForm({ ...form, subject: e.target.value })}
              />
            </Form.Group>

            <Form.Group>
              <Form.Label className='fw-semibold'>Mensagem <span className='text-danger'>*</span></Form.Label>
              <Form.Control
                as='textarea'
                rows={6}
                ref={messageRef}
                value={form.message}
                maxLength={MESSAGE_MAX_LENGTH}
                disabled={busy}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
              />
              <div className='mt-2'>
                <NameTokenButtons inputRef={messageRef} value={form.message} onChange={(message) => setForm({ ...form, message })} disabled={busy} maxLength={MESSAGE_MAX_LENGTH} />
              </div>
              <Form.Text>Os botões inserem o nome do paciente onde está o cursor. {'{{primeiro_nome}}'} e {'{{nome}}'} também funcionam no assunto.</Form.Text>
            </Form.Group>

            <div className='rounded p-3' style={{ backgroundColor: 'var(--app-surface-alt)', border: '1px solid var(--app-border)' }}>
              <small className='text-muted fw-semibold d-block mb-2'>Pré-visualização (paciente de exemplo: Maria da Silva)</small>
              <div className='fw-semibold mb-2' style={{ fontSize: 14 }}>{fillNames(form.subject) || 'Feliz aniversário!'}</div>
              {form.message.trim() ?
                <div className='birthdays-greeting-preview'>{fillNames(form.message.trim())}</div>
                :
                <div className='fst-italic text-muted' style={{ fontSize: 14 }}>A mensagem aparecerá aqui.</div>
              }
              {imagePreview ? <img src={imagePreview} alt='Imagem do e-mail' className='birthdays-greeting-image mt-3' /> : ''}
            </div>

            <div className='border rounded p-3 d-flex flex-column gap-2'>
              <div className='d-flex align-items-center gap-2 fw-semibold'><LuImage /> Imagem do e-mail</div>
              <small className='text-muted'>Opcional. Aparece no corpo do e-mail, abaixo da mensagem. JPG, PNG ou GIF de até {IMAGE_MAX_SIZE_MB} MB.</small>
              <div className='d-flex flex-wrap gap-2'>
                <input ref={fileRef} type='file' accept='.jpg,.jpeg,.png,.gif' hidden onChange={handleSelectImage} />
                <Button variant='outline-secondary' size='sm' disabled={busy} onClick={() => fileRef.current?.click()}>
                  {imagePreview ? 'Trocar imagem' : 'Escolher imagem'}
                </Button>
                {imagePreview ?
                  <Button variant='outline-danger' size='sm' disabled={busy} onClick={() => setImageChange(config.image_url ? { kind: 'remove' } : { kind: 'unchanged' })}>
                    Remover
                  </Button>
                  : ''}
                {imageChange.kind === 'unchanged' ? '' : <small className='text-warning-emphasis align-self-center'>A imagem muda ao salvar.</small>}
              </div>
            </div>

            <div className='border rounded p-3 d-flex flex-column gap-2'>
              <span className='fw-semibold'>Enviar e-mail de teste</span>
              <small className='text-muted'>Envia um exemplo com a configuração salva, usando o nome Maria da Silva.</small>
              <div className='d-flex flex-column flex-sm-row gap-2'>
                <Form.Control
                  type='email'
                  value={testEmail}
                  disabled={busy}
                  placeholder='email@exemplo.com'
                  onChange={(e) => setTestEmail(e.target.value)}
                />
                <Button variant='outline-secondary' className='d-flex align-items-center justify-content-center gap-2 text-nowrap' disabled={busy} onClick={handleSendTest}>
                  {sendingTest ? <Spinner animation='border' size='sm' /> : <LuSend />} Enviar teste
                </Button>
              </div>
              {dirty ? <small className='text-warning-emphasis'>Há alterações não salvas: salve antes para testar a versão nova.</small> : ''}
            </div>

            {confirmDispatch ?
              <Alert ref={confirmRef} variant='warning' className='m-0'>
                <div className='fw-semibold mb-1'>Enviar os e-mails de aniversário agora?</div>
                <div style={{ fontSize: 14 }}>
                  Envia na hora, com a configuração salva, para os pacientes ativos que fazem aniversário hoje e ainda não receberam neste ano.
                  {dirty ? ' Há alterações não salvas, que não entram neste envio.' : ''}
                </div>
                <div className='d-flex justify-content-end gap-2 mt-2'>
                  <Button variant='outline-secondary' size='sm' disabled={dispatching} onClick={() => setConfirmDispatch(false)}>Cancelar</Button>
                  <Button variant='warning' size='sm' className='d-flex align-items-center gap-2' disabled={dispatching} onClick={handleDispatch}>
                    {dispatching ? <Spinner animation='border' size='sm' /> : <FaBirthdayCake />} Enviar agora
                  </Button>
                </div>
              </Alert>
              : ''}
          </>
        }
      </Modal.Body>

      <Modal.Footer className='d-flex justify-content-between'>
        <Button variant='outline-secondary' className='d-flex align-items-center gap-2' disabled={busy || !config || confirmDispatch} onClick={() => setConfirmDispatch(true)}>
          <FaBirthdayCake /> Enviar agora
        </Button>
        <div className='d-flex gap-2'>
          <Button variant='outline-secondary' disabled={busy} onClick={onHide}>Cancelar</Button>
          <Button variant='success' className='d-flex align-items-center gap-2' disabled={busy || !config} onClick={handleSave}>
            {saving ? <Spinner animation='border' size='sm' /> : ''} Salvar
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  )
}

export default BirthdayGreetingsModal
